import { Breakpoint } from "@mui/material";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import Localization from "../core/Localization";
import { useWidth, WidthProps } from "../core/Responsive";
import Sys from "../core/Sys";
import FocusTracker from "../coreui/FocusTracker";
import Table, { TableChildProps, TableProps } from "../coreui/Table";
import AsyncData, {
  GetDataResponse,
  LoadingState,
} from "../coreui/table/AsyncData";
import PaneRow from "../models/PaneRow";
import RoundTripService from "../services/RoundTripService";
import PaneDataStore from "../stores/PaneDataStore";
import { AccessLevel } from "./AccessLevel";
import GridColumn, { GridColumnConfigProperties } from "./Columns/GridColumn";
import ProjectCurrentJobColumn, {
  ProjectCurrentJobHeader,
} from "./Columns/ProjectCurrentJobColumn";
import ErrorBoundary from "./ErrorBoundary";
import { ProjectGridControlChildProps } from "./ProjectGridControl";
import ProjectGridVerticalHierarchy from "./ProjectGridVerticalHierarchy";

interface ConfigProperties {
  cardDepth: number;
  columns: GridColumnConfigProperties[];
  contentDataId: string;
  data?: object;
  dataId: string;
  footerToolbar?: object;
  headerToolbar?: object;
  hierarchyDepth: { [key in Breakpoint]: "Full" | "Partial" };
  isVerticalLayout: boolean;
  name: string;
  propagated: ProjectGridControlChildProps;
  tableKey: string;
}

interface RuntimeProperties {
  accessLevel: AccessLevel;
}

interface State {
  isMounted: boolean;
}

export class ProjectGrid extends React.Component<
  ConfigProperties & WidthProps,
  State
> {
  private static currentJobColumnId: string = "_projectCurrentJobColumn";

  private asyncDataRef: React.RefObject<AsyncData>;
  private contentDataId: string;
  private currentHierarchyDepth: "Full" | "Partial" = "Full";
  private dataId: string;
  private focusTrackerRef: React.RefObject<FocusTracker>;
  private loadingHasBeenNotified: boolean = false;
  private name: string;
  private populate: ((rows: PaneRow[]) => void) | null = null;
  private propagated: TableChildProps;
  private tableProps: TableProps;

  public static getPartialHierarchyRows(rows: PaneRow[]): PaneRow[] {
    const currentRowIndex: number = rows.findIndex((row) => row.isCurrentJob!);

    if (currentRowIndex === -1) {
      // Occurs if the grid is not yet loaded
      return [];
    }

    let parentRowIndex: number = currentRowIndex;
    for (let i = currentRowIndex; i >= 0; i--) {
      if (rows[i].hierarchyLevel! < rows[currentRowIndex].hierarchyLevel!) {
        parentRowIndex = i;
        break;
      }
    }

    const currentRowLevel: number = rows[currentRowIndex].hierarchyLevel!;
    let state: string = "AddParent";
    const partialHierarchyRows: PaneRow[] = [];

    for (let i = parentRowIndex; i < rows.length; i++) {
      const row: PaneRow = rows[i];

      if (state === "AddParent") {
        if (row.hierarchyLevel! < currentRowLevel) {
          partialHierarchyRows.push(row);

          continue;
        }

        state = "AddSiblings";
      }

      if (state === "AddChildren") {
        if (row.hierarchyLevel! === currentRowLevel + 1) {
          partialHierarchyRows.push(row);

          continue;
        }

        if (row.hierarchyLevel! > currentRowLevel + 1) {
          continue;
        }

        state = "AddSiblings";
      }

      if (state === "AddSiblings") {
        if (row.hierarchyLevel! < currentRowLevel) {
          break;
        }

        if (row.hierarchyLevel! === currentRowLevel) {
          partialHierarchyRows.push(row);
        }

        if (row.isCurrentJob!) {
          state = "AddChildren";
        }
      }
    }

    return partialHierarchyRows;
  }

  public constructor(props: ConfigProperties & WidthProps) {
    super(props);

    makeObservable<
      ProjectGrid,
      "currentHierarchyDepth" | "setCurrentHierarchyDepth"
    >(this, {
      currentHierarchyDepth: observable,
      setCurrentHierarchyDepth: action,
    });

    this.state = { isMounted: false };
    this.asyncDataRef = React.createRef();
    this.contentDataId = props.contentDataId;
    this.dataId = props.dataId;
    this.focusTrackerRef = React.createRef();
    this.name = props.name;
    this.setCurrentHierarchyDepth(props.hierarchyDepth[props.width]);

    this.propagated = {
      parentTable: {
        cardDepth: props.cardDepth,
        columns: props.columns,
        configProps: {
          contentDataId: props.contentDataId,
          data: props.data,
          dataId: props.dataId,
          name: props.name,
          tableKey: props.tableKey,
        },
        hasRelatedEditDialog: false,
        isDocumentGrid: false,
        isProjectGrid: true,
        populateData: () => this.populateData(),
      },
    } as TableChildProps;

    this.tableProps = {
      cardDepth: props.cardDepth,
      cellEdit: false,
      columns: [],
      contentDataId: props.contentDataId,
      dataId: props.dataId,
      footerToolbarChild: props.footerToolbar,
      getAccessibleDescription: this.getAccessibleDescription,
      headerToolbarChild: props.headerToolbar,
      ignoreBusinessErrors: true,
      isColumnFlex: this.isColumnFlex,
      isColumnVisible: this.isColumnVisible,
      minRowHeight: GridColumn.getColumnsMinRowHeight(props.columns),
      name: props.name,
      propagated: this.propagated,
      resetPageOnPopulate: true,
      setPopulate: (populate) => (this.populate = populate),
      showNoData: false,
    };
  }

  private announceLoadingComplete = (): void => {
    setTimeout(() => {
      Sys.announce(
        Localization.getBuiltInMessage("DataTable.loadComplete", {
          gridDescription:
            this.props.propagated.parentProjectGridControl.description,
        }),
        true
      );
    }, 1000);
  };

  private announceLoadingStarted = (): void => {
    Sys.announce(
      Localization.getBuiltInMessage("DataTable.loadStarted", {
        gridDescription:
          this.props.propagated.parentProjectGridControl.description,
      }),
      true
    );
  };

  private getAccessibleDescription(rowCount: number): string {
    if (rowCount === 0) {
      return Localization.getBuiltInMessage("DataTable.tableRowCountZero");
    }

    if (rowCount === 1) {
      return Localization.getBuiltInMessage("DataTable.tableRowCountSingle");
    }

    return Localization.getBuiltInMessage("DataTable.tableRowCountMultiple", {
      count: rowCount,
    });
  }

  private getData = () => {
    const row = PaneRow.get(this.dataId)!;

    return RoundTripService.partialDataRetrevial<GetDataResponse>(
      `ProjectGrid/GetRowsData/${row.rowKey}/${this.dataId}/${this.name}`
    );
  };

  private isColumnFlex = (colId: string) => {
    if (colId === ProjectGrid.currentJobColumnId) {
      return true;
    }

    return GridColumn.isColumnFlex(this.props.columns, colId);
  };

  private isColumnVisible = (colId: string, breakpoint: string) => {
    if (colId === ProjectGrid.currentJobColumnId) {
      return true;
    }

    return GridColumn.isColumnVisible(this.props.columns, colId, breakpoint);
  };

  private onFocusChanged = (isFocused: boolean): void => {
    if (!this.asyncDataRef) {
      return;
    }

    if (isFocused) {
      const loadingState: LoadingState =
        this.asyncDataRef.current!.getLoadingState();
      if (loadingState.isLoadingData || loadingState.isPopulatingData) {
        this.announceLoadingStarted();
      }
    } else {
      this.loadingHasBeenNotified = false;
    }
  };

  private onIsLoadingChanged = (
    isLoadingData: boolean,
    isPopulatingData: boolean
  ): void => {
    if (!this.focusTrackerRef) {
      return;
    }

    if (!this.focusTrackerRef.current?.isFocused()) {
      return;
    }

    if (isLoadingData || isPopulatingData) {
      if (!this.loadingHasBeenNotified) {
        this.announceLoadingStarted();
        this.loadingHasBeenNotified = true;
      }
    } else {
      this.announceLoadingComplete();
      this.loadingHasBeenNotified = false;
    }
  };

  private populateData = () => {
    let rows: PaneRow[] = PaneDataStore.getPaneCollection(this.contentDataId);

    if (this.currentHierarchyDepth === "Partial") {
      rows = ProjectGrid.getPartialHierarchyRows(rows);
    }

    if (this.populate !== null) {
      this.populate(rows);
    }
  };

  private setCurrentHierarchyDepth(hierarchyDepth: "Full" | "Partial"): void {
    this.currentHierarchyDepth = hierarchyDepth;
  }

  public componentDidMount(): void {
    this.tableProps.columns.push({
      autoHeight: false,
      cellClass: "cx-cell",
      cellRenderer: ProjectCurrentJobColumn,
      cellStyle: {
        overflow: "visible",
        padding: "0px",
      },
      colId: ProjectGrid.currentJobColumnId,
      editable: false,
      headerComponent: ProjectCurrentJobHeader,
      lockPinned: true,
      minWidth: 64,
      resizable: false,
      sortable: false,
      suppressSizeToFit: true,
      width: 64,
    });

    for (const column of this.props.columns) {
      this.tableProps.columns.push(
        GridColumn.getColumnDef(column, this.props.columns, this.propagated)
      );
    }

    this.setState({ isMounted: true });
  }

  public componentDidUpdate(prevProps: ConfigProperties & WidthProps): void {
    if (prevProps.width !== this.props.width) {
      this.setCurrentHierarchyDepth(
        this.props.hierarchyDepth[this.props.width]
      );
      setTimeout(() => this.populateData());
    }
  }

  public componentWillUnmount(): void {
    this.setState({ isMounted: false });
  }

  public render(): React.ReactNode {
    const row = PaneRow.get(this.props.dataId);
    if (!row) {
      return null;
    }

    const widget = row.getWidgetT<null, RuntimeProperties>(this.props.name);

    if (widget.properties.accessLevel === AccessLevel.hidden) {
      return null;
    }

    return (
      <FocusTracker
        onFocusChanged={this.onFocusChanged}
        ref={this.focusTrackerRef}
      >
        <ErrorBoundary title={this.props.name}>
          <AsyncData
            contentDataId={this.props.contentDataId}
            dataId={this.props.dataId}
            getData={this.getData}
            onIsLoadingChanged={this.onIsLoadingChanged}
            populateData={this.populateData}
            ref={this.asyncDataRef}
          />
          {this.props.isVerticalLayout ? (
            <ProjectGridVerticalHierarchy.render
              cardDepth={this.props.cardDepth}
              dataId={this.props.contentDataId}
              headerToolbarChild={this.props.headerToolbar}
              propagated={this.propagated}
            />
          ) : (
            // FUTURE
            // An isMounted flag is used to ensure the Table constructor only
            // fires when there are actually columns for the table. The root
            // cause is that the Table component is doing rendering logic in
            // the constructor, which is counter to the architecture of React.
            // At "some point", likely when moving to the MUI Grid component,
            // this should be reworked.
            this.state.isMounted && (
              <Table
                {...this.tableProps}
                aria-label={
                  this.props.propagated.parentProjectGridControl.description
                }
                tableKey={this.props.tableKey}
              />
            )
          )}
        </ErrorBoundary>
      </FocusTracker>
    );
  }
}

// FUTURE
// This wrapper component was created to avoid the scope of converting this
// component to a hooks component during the MUI 5 upgrade. When the legacy
// component is converted to a hooks component, this wrapper can be removed and
// hooks it calls can be called by the converted component directly.
const Observer = observer(ProjectGrid);
export default function Wrapped(props: ConfigProperties): JSX.Element {
  const width = useWidth();
  return <Observer {...props} width={width} />;
}
